// Osobní nastavení uživatele (kontrakt `uzivatel_nastaveni`: GET/PUT /ja/nastaveni): téma, šířka
// a sbalení levé lišty, zavřené skupiny, oblíbené ★ (Rychlé přístupy), osobní pořadí menu.
// Záloha v localStorage: platí, dokud server neodpoví; po úspěšné odpovědi má server přednost.
// Endpoint může na starším backendu chybět (404) — pak se pracuje jen s localStorage.
import type { Schema } from '@ecoser/shared';
import { api, ChybaApi } from './klient';

/** Typy z kontraktu (JSON sloupce jsou v něm jen `object`; skutečný tvar je pole klíčů). */
export type UzivatelNastaveni = Schema<'UzivatelNastaveni'>;
export type UzivatelNastaveniVstup = Schema<'UzivatelNastaveniVstup'>;

export type Tema = 'light' | 'dark';

/** Nastavení tak, jak s ním pracuje UI (pole místo `object` z kontraktu). */
export interface NastaveniUzivatele {
  theme: Tema;
  /** Šířka lišty v px (prototyp 236, rozsah 170–420). */
  rail_width: number;
  rail_collapsed: boolean;
  /** Názvy (klíče) zavřených skupin v liště. */
  rail_groups_closed: string[];
  /** Klíče sekcí označených ★ — Rychlé přístupy na Domů a skupina Oblíbené v liště. */
  quick_links: string[];
  /** Osobní pořadí klíčů sekcí; null = oficiální pořadí serveru. */
  menu_osobni_poradi: string[] | null;
  /** Poslední otevřená sekce (zatím se neukládá; přijde s Nastavením v F2). */
  posledni_sekce: string | null;
  /** Kanály upozornění (12.5): e‑mail se souhrnem nepřečtených a push na zařízení; zvonek je vždy. */
  notifikace_email: boolean;
  notifikace_push: boolean;
  /**
   * Jen ke čtení (server, 05/4620 b): typy upozornění, které účtu chodí e‑mailem vždy i s vypnutým e‑mailem —
   * `zakazka_nebezpecny_odpad` u příjemců upozornění na nebezpečný odpad. Na server se neposílá.
   */
  email_vzdy?: string[];
  /** Id novinek potvrzených v okně „Co je nového“ (`novinky/`, migrace 2445). */
  novinky_prectene: string[];
  /**
   * Kdy uživatel potvrdil okno „Dotazy na asistenta vidí jen vy, administrátor a jednatel.“ (05/2520 c, migrace 3521);
   * null = okno se před prvním dotazem ještě ukáže. Čas zapisuje server jen jednou.
   */
  asistent_soukromi_potvrzeno: string | null;
  /**
   * Filtr, seskupení a řazení seznamu zakázek (05/3236 c, migrace 4366) — pamatuje se u účtu na všech zařízeních;
   * tvar hlídá `sekce/zakazky/filtr-seznamu.ts`, null = výchozí filtr.
   */
  zakazky_seznam: Record<string, unknown> | null;
}

export const LISTA_SIRKA_VYCHOZI = 236;
export const LISTA_SIRKA_MIN = 170;
export const LISTA_SIRKA_MAX = 420;
export const LISTA_SIRKA_SBALENA = 58;

export const NASTAVENI_VYCHOZI: Readonly<NastaveniUzivatele> = {
  theme: 'light',
  rail_width: LISTA_SIRKA_VYCHOZI,
  rail_collapsed: false,
  rail_groups_closed: [],
  quick_links: [],
  menu_osobni_poradi: null,
  posledni_sekce: null,
  notifikace_email: true,
  notifikace_push: true,
  novinky_prectene: [],
  asistent_soukromi_potvrzeno: null,
  zakazky_seznam: null,
};

export const KLIC_NASTAVENI = ['ja', 'nastaveni'] as const;

function poleRetezcu(hodnota: unknown): string[] {
  if (Array.isArray(hodnota)) return hodnota.filter((x): x is string => typeof x === 'string');
  // Starší tvar `{ "0": "a", "1": "b" }` (JSON object v DB) — vezmeme hodnoty.
  if (hodnota && typeof hodnota === 'object') {
    return Object.values(hodnota).filter((x): x is string => typeof x === 'string');
  }
  return [];
}

/** Omezí šířku lišty do rozsahu kontraktu (CHECK 170–420). */
export function omezitSirkuListy(sirka: number): number {
  if (!Number.isFinite(sirka)) return LISTA_SIRKA_VYCHOZI;
  return Math.max(LISTA_SIRKA_MIN, Math.min(LISTA_SIRKA_MAX, Math.round(sirka)));
}

/** Převede odpověď serveru nebo obsah úložiště na bezpečný tvar pro UI (neznámé hodnoty → výchozí). */
export function normalizovatNastaveni(vstup: unknown): NastaveniUzivatele {
  const z = (vstup && typeof vstup === 'object' ? vstup : {}) as Record<string, unknown>;
  const poradi = z.menu_osobni_poradi == null ? null : poleRetezcu(z.menu_osobni_poradi);
  return {
    theme: z.theme === 'dark' ? 'dark' : 'light',
    rail_width: omezitSirkuListy(typeof z.rail_width === 'number' ? z.rail_width : NaN),
    rail_collapsed: z.rail_collapsed === true,
    rail_groups_closed: poleRetezcu(z.rail_groups_closed),
    quick_links: poleRetezcu(z.quick_links),
    menu_osobni_poradi: poradi && poradi.length > 0 ? poradi : null,
    posledni_sekce:
      typeof z.posledni_sekce === 'string' && z.posledni_sekce ? z.posledni_sekce : null,
    // Starší server bez kroku 12.5 pole neposílá → výchozí zapnuto (05/921).
    notifikace_email: z.notifikace_email !== false,
    notifikace_push: z.notifikace_push !== false,
    email_vzdy: poleRetezcu(z.email_vzdy),
    novinky_prectene: poleRetezcu(z.novinky_prectene),
    asistent_soukromi_potvrzeno:
      typeof z.asistent_soukromi_potvrzeno === 'string' && z.asistent_soukromi_potvrzeno
        ? z.asistent_soukromi_potvrzeno
        : null,
    zakazky_seznam:
      z.zakazky_seznam && typeof z.zakazky_seznam === 'object' && !Array.isArray(z.zakazky_seznam)
        ? (z.zakazky_seznam as Record<string, unknown>)
        : null,
  };
}

/** GET /ja/nastaveni; 404 (endpoint nebo řádek zatím není) → null, jiné chyby vyhodí. */
export async function nacistNastaveni(signal?: AbortSignal): Promise<NastaveniUzivatele | null> {
  try {
    const data = await api.get<UzivatelNastaveni>('/ja/nastaveni', { signal });
    return normalizovatNastaveni(data);
  } catch (chyba) {
    if (chyba instanceof ChybaApi && chyba.status === 404) return null;
    throw chyba;
  }
}

/** Tělo PUT podle kontraktu `UzivatelNastaveniVstup` (JSON sloupce jako pole). */
export function teloNastaveni(
  zamestnanecId: string,
  nastaveni: NastaveniUzivatele,
): UzivatelNastaveniVstup {
  const telo = {
    zamestnanec_id: zamestnanecId,
    theme: nastaveni.theme,
    rail_width: omezitSirkuListy(nastaveni.rail_width),
    rail_collapsed: nastaveni.rail_collapsed,
    rail_groups_closed: nastaveni.rail_groups_closed,
    quick_links: nastaveni.quick_links,
    // `null` vrací osobní pořadí na oficiální (server sloupec vynuluje); `undefined` by ho nechalo beze změny.
    menu_osobni_poradi: nastaveni.menu_osobni_poradi,
    posledni_sekce: nastaveni.posledni_sekce ?? undefined,
    notifikace_email: nastaveni.notifikace_email,
    notifikace_push: nastaveni.notifikace_push,
    novinky_prectene: nastaveni.novinky_prectene,
    asistent_soukromi_potvrzeno: nastaveni.asistent_soukromi_potvrzeno ?? undefined,
    // null = výchozí filtr (server sloupec vynuluje).
    zakazky_seznam: nastaveni.zakazky_seznam,
  };
  // Kontrakt typuje JSON sloupce jako prázdný `object`; skutečná data jsou pole klíčů.
  return telo as unknown as UzivatelNastaveniVstup;
}

/** PUT /ja/nastaveni; vrací, co server uložil (má přednost před lokální kopií). */
export async function ulozitNastaveni(
  zamestnanecId: string,
  nastaveni: NastaveniUzivatele,
  moznosti: { keepalive?: boolean } = {},
): Promise<NastaveniUzivatele> {
  const data = await api.put<UzivatelNastaveni>(
    '/ja/nastaveni',
    teloNastaveni(zamestnanecId, nastaveni),
    { keepalive: moznosti.keepalive },
  );
  return normalizovatNastaveni(data);
}

// --- Záloha v localStorage (per zaměstnanec, aby se nastavení nepletlo mezi účty na jednom tabletu) ---

export function klicUloziste(zamestnanecId: string): string {
  return `ecoser.nastaveni.${zamestnanecId}`;
}

export function precistNastaveniZUloziste(zamestnanecId: string): NastaveniUzivatele | null {
  try {
    const text = window.localStorage.getItem(klicUloziste(zamestnanecId));
    return text ? normalizovatNastaveni(JSON.parse(text)) : null;
  } catch {
    return null;
  }
}

export function zapsatNastaveniDoUloziste(zamestnanecId: string, nastaveni: NastaveniUzivatele) {
  try {
    window.localStorage.setItem(klicUloziste(zamestnanecId), JSON.stringify(nastaveni));
  } catch {
    // Soukromé okno / plné úložiště — záloha je jen pohodlí, bez ní se nic neztratí.
  }
}
